import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, money, newGuest, USER_ID } from "../api";
import { useSeo } from "../seo";
import { SectionTitle } from "../components";
import { AdminVoice } from "./AdminVoice";
import { AdminQuizzes } from "./AdminQuizzes";
import { AdminStories } from "./AdminStories";
import { AdminClips } from "./AdminClips";
import { AdminExtMenu, PosDiagnostics } from "./AdminExtMenu";
import { AdminArcade } from "./AdminArcade";
import { AdminAccess } from "./AdminAccess";
import { AdminBrands } from "./AdminBrands";
import { AdminSite } from "./AdminSite";
import { AdminVisits } from "./AdminVisits";
import AdminCream from "./AdminCream";

const TABS = ["WEEKLY DIGEST", "SITE", "DISH INTELLIGENCE", "EXTENDED MENU", "ARCADE", "BRANDS", "ANALYTICS", "ORDERS", "SPECIALS", "REWARDS", "VISITS", "QUIZZES", "STORY STUDIO", "CLIPS", "VOICE", "FEATURE FLAGS", "TABLET", "ACCESS"];
const MANAGER_TABS = ["DISH INTELLIGENCE", "VOICE", "ORDERS"];

const PosPingPanel = () => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const ping = async () => {
    setBusy(true);
    setRes(null);
    try {
      const r = await api.post("/admin/pos-test-ping", {});
      setRes(r.data);
    } catch (e) {
      setRes({ verdict: e.response?.data?.detail || "The test could not run — please try again.", ok: false });
    }
    setBusy(false);
  };
  return (
    <div data-testid="pos-ping-panel" className="rounded-xl bg-[#121212] border border-white/10 p-5 mb-4">
      <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">POS CONNECTION</p>
      <p className="text-xs text-white/50 mb-3">Sends a signed test ping to VeerGiGalla — deliberately not an order, nothing is created. Tells you in plain words if the POS answers and whether it accepts our signature.</p>
      <button data-testid="pos-ping-btn" onClick={ping} disabled={busy}
        className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 py-3 tracking-widest disabled:opacity-50">
        {busy ? "PINGING THE POS…" : "TEST POS CONNECTION"}
      </button>
      {res && (
        <div data-testid="pos-ping-result" className={`mt-3 rounded-xl border p-3 text-sm ${res.ok && res.signature_accepted ? "border-[#2ECC71]/50 bg-[#2ECC71]/10" : "border-[#FF5A4E]/50 bg-[#FF5A4E]/10"}`}>
          <p className="font-bold">{res.verdict}</p>
          <p className="text-xs text-white/50 mt-1.5">
            HTTP {res.http_status ?? "no answer"}{typeof res.took_ms === "number" ? ` · ${res.took_ms} ms` : ""}
            {res.signature_accepted === true && " · signature ACCEPTED ✓"}
            {res.signature_accepted === false && " · signature REJECTED ✗"}
          </p>
          {res.pos_reply && <p data-testid="pos-ping-reply" className="text-xs text-white/45 mt-1 font-mono break-all">POS replied: {res.pos_reply}</p>}
        </div>
      )}
    </div>
  );
};

const Delta = ({ v }) => (
  <span className={`text-[10px] font-bold ${v > 0 ? "text-green-400" : v < 0 ? "text-[#E25822]" : "text-white/40"}`}>
    {v > 0 ? `▲ +${v}` : v < 0 ? `▼ ${v}` : "— 0"} vs last week
  </span>
);

const PosPushBanner = () => {
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);
  useEffect(() => { api.get("/admin/menu-push-banner").then((r) => setB(r.data)).catch(() => {}); }, []);
  if (!b?.found || gone || localStorage.getItem("xp_seen_push") === b.push_id) return null;
  const when = new Date(b.ts).toLocaleString("en-US", { timeZone: "America/Chicago" });
  const usd = (v) => (isNaN(Number(v)) ? v : `$${Number(v).toFixed(2)}`);
  const words = (c) =>
    c.field === "price" ? `price ${usd(c.from)} → ${usd(c.to)}`
      : c.field === "available" ? (c.to === false ? "TAKEN OFF the menu" : "back ON the menu")
        : c.field === "image" ? "photo changed"
          : c.field === "name" ? `renamed “${c.from}” → “${c.to}”`
            : c.field === "category" ? `category ${c.from || "—"} → ${c.to}`
              : "description replaced";
  return (
    <div data-testid="pos-push-banner" className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-2.5 mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] tracking-wide">
          <span className="font-black text-[#D4AF37]">POS MENU PUSH</span>{" "}
          <span className="font-mono">{b.push_id}</span> · {when} (Chicago) ·{" "}
          <span className="font-bold">{b.changed_count ?? 0} changed · {b.created_count ?? 0} new · {b.hidden_count ?? 0} hidden · {b.combos ?? 0} combos</span>
          {(b.photo_check?.warnings || []).length > 0 && (
            <span data-testid="pos-push-photo-warning-count" className="font-bold text-[#F0C742]"> · {b.photo_check.warnings.length} photo warning{b.photo_check.warnings.length === 1 ? "" : "s"}</span>
          )}
        </p>
        <span className="flex gap-2 shrink-0">
          <button data-testid="pos-push-banner-details" onClick={() => setOpen(!open)}
            className="text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-1">
            {open ? "HIDE" : "WHAT CHANGED"}
          </button>
          <button data-testid="pos-push-banner-dismiss"
            onClick={() => { localStorage.setItem("xp_seen_push", b.push_id); setGone(true); }}
            className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-3 py-1">
            DISMISS
          </button>
        </span>
      </div>
      {open && (
        <div data-testid="pos-push-banner-detail-list" className="mt-2 space-y-0.5 text-[10px] font-mono max-h-48 overflow-y-auto">
          {(b.change_rows || []).map((r) => (
            <p key={r.slug}><span className="text-[#D4AF37]">{r.name}</span> — {r.changes.map(words).join(" · ")}</p>
          ))}
          {(b.created_names || []).length > 0 && <p><span className="text-green-400 font-bold">NEW:</span> {b.created_names.join(", ")}</p>}
          {(b.hidden_names || []).length > 0 && <p><span className="text-[#E25822] font-bold">HIDDEN:</span> {b.hidden_names.join(", ")}</p>}
          {(b.photo_check?.warnings || []).length > 0 && (
            <div data-testid="pos-push-photo-warnings" className="pt-1">
              <p className="text-[#F0C742] font-bold">PHOTO CHECK — a heads-up only, nothing was blocked. Fix these on the dish in the POS and push again:</p>
              {b.photo_check.warnings.map((w2, i) => (
                <p key={i}>{w2.name}{w2.size ? ` (${w2.size})` : ""} — {w2.problem}</p>
              ))}
            </div>
          )}
          {(b.change_rows || []).length === 0 && (b.created_names || []).length === 0 && (b.hidden_names || []).length === 0 &&
            <p className="text-white/50">No field-level differences — the push matched what was already on the Passport.</p>}
        </div>
      )}
    </div>
  );
};

const PosMenuKeyCard = () => {
  const [st, setSt] = useState(null);
  const [revealed, setRevealed] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/pos-menu-key").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (st?.key && !window.confirm("Rotate the POS menu key? The old key stops working IMMEDIATELY — the POS push will fail until the new key is in its secrets.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/pos-menu-key");
      setRevealed(r.data.key);
      setCopied(false);
      load();
    } catch { toast.error("Could not issue the key"); }
    setBusy(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(revealed); setCopied(true); } catch { toast.error("Copy failed — select and copy by hand"); }
  };
  if (!st) return null;
  return (
    <div data-testid="pos-menu-key-card" className="border-t border-white/10 mt-5 pt-5">
      <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">POS MENU PUSH KEY</p>
      <p className="text-xs text-white/50 mb-3">
        The POS is the only menu authority. It pushes the FULL menu (categories, dishes, photos, prices, combos, on/off)
        with this key — each push replaces the whole menu; dishes it drops are hidden, never deleted.
      </p>
      <p data-testid="pos-menu-source-status" className="text-xs mb-3">
        Menu source now: {st.pos_live
          ? <span className="text-green-400 font-bold">POS LIVE — {st.push_count} push{st.push_count === 1 ? "" : "es"}, last {st.last_push?.ts ? new Date(st.last_push.ts).toLocaleString("en-US", { timeZone: "America/Chicago" }) + " (Chicago)" : "—"}</span>
          : <span className="text-white/70 font-bold">LOCAL MENU — unchanged until the first POS push lands</span>}
      </p>
      {st.last_push && (
        <p data-testid="pos-last-push-summary" className="text-xs text-white/50 mb-3">
          Last push {st.last_push.push_id}: {st.last_push.received} dishes · {st.last_push.created_count} new · {st.last_push.updated} updated · {st.last_push.hidden_count} hidden · {st.last_push.combos} combos
        </p>
      )}
      {st.key ? (
        <p data-testid="pos-key-status" className="text-xs text-white/50 mb-3">
          Key active (id <span className="font-mono">{st.key.key_id}</span>) · issued {new Date(st.key.created_at).toLocaleString("en-US", { timeZone: "America/Chicago" })} (Chicago) ·
          {st.key.last_used_at ? ` last used ${new Date(st.key.last_used_at).toLocaleString("en-US", { timeZone: "America/Chicago" })} (Chicago) · ${st.key.use_count} uses` : " never used yet"}
        </p>
      ) : (
        <p data-testid="pos-key-status" className="text-xs text-white/50 mb-3">No key issued yet.</p>
      )}
      {revealed ? (
        <div data-testid="pos-key-reveal" className="rounded-xl border border-[#E25822]/50 bg-[#E25822]/10 p-4 mb-3">
          <p className="text-[10px] tracking-widest text-[#E25822] font-bold mb-2">SHOWN ONCE — NEVER AGAIN</p>
          <p className="font-mono text-xs break-all select-all mb-3">{revealed}</p>
          <div className="flex gap-2">
            <button data-testid="pos-key-copy" onClick={copy}
              className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-5 py-2 tracking-widest">
              {copied ? "COPIED ✓" : "COPY KEY"}
            </button>
            <button data-testid="pos-key-done" onClick={() => setRevealed("")}
              className="border border-white/20 text-white/70 text-xs font-bold rounded-full px-5 py-2 tracking-widest">
              I'VE STORED IT — HIDE
            </button>
          </div>
          <p className="text-[11px] text-white/50 mt-2">Put it in the POS secrets now as the X-API-Key for the menu push. It is not stored anywhere on the Passport — only a hash. If it's lost, rotate.</p>
        </div>
      ) : (
        <button data-testid="pos-key-generate" onClick={generate} disabled={busy}
          className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 py-3 tracking-widest disabled:opacity-50">
          {busy ? "WORKING…" : st.key ? "ROTATE KEY (KILLS THE OLD ONE)" : "GENERATE POS MENU KEY"}
        </button>
      )}
      <p className="text-[11px] text-white/40 mt-3">
        Payload format for the POS team: GET <span className="font-mono">/api/integrations/veergi-galla/menu-replace/schema</span> with the same key.
      </p>
    </div>
  );
};

const PasscodeGate = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const submit = async () => {
    try {
      const r = await api.post("/admin/unlock", { passcode: code });
      sessionStorage.setItem("xp_admin_ok", "1");
      sessionStorage.setItem("xp_admin_role", r.data.role || "owner");
      sessionStorage.setItem("xp_admin_token", r.data.token || "");
      sessionStorage.removeItem("xp_admin_kicked");
      onUnlock();
    } catch {
      setError(true);
      setCode("");
    }
  };
  return (
    <div data-testid="admin-passcode-gate" className="px-6 pt-24 text-center">
      <Lock size={28} className="mx-auto text-[#D4AF37]" />
      <p className="text-[10px] tracking-[0.35em] text-[#D4AF37] mt-4">STAFF ONLY</p>
      <h1 className="font-display text-3xl font-bold mt-1">Enter passcode</h1>
      <input
        data-testid="admin-passcode-input"
        type="password"
        inputMode="numeric"
        value={code}
        onChange={(e) => { setCode(e.target.value); setError(false); }}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="mt-6 w-48 mx-auto block bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-center text-lg tracking-[0.5em] focus:outline-none focus:border-[#D4AF37]/60"
      />
      {error && <p data-testid="admin-passcode-error" className="text-xs text-[#E25822] mt-3">Wrong passcode</p>}
      <button data-testid="admin-passcode-submit" onClick={submit}
        className="mt-5 bg-[#D4AF37] text-black text-xs font-bold rounded-full px-8 py-3 tracking-widest">
        UNLOCK
      </button>
    </div>
  );
};

const NumField = ({ label, value, onChange, testId }) => (
  <label className="flex items-center justify-between gap-2 text-xs">
    <span className="text-white/50 w-16">{label}</span>
    <input data-testid={testId} type="number" min="1" max="5" value={value}
      onChange={(e) => onChange(+e.target.value)}
      className="w-14 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-1.5 text-center" />
  </label>
);

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const parseTables = (v) => {
  const s = v.trim();
  if (!s) return [];
  if (/^\d+$/.test(s)) return Array.from({ length: Math.min(+s, 60) }, (_, i) => String(i + 1));
  return s.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 60);
};

export default function AdminX() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("xp_admin_ok") === "1");
  const [advanced, setAdvanced] = useState(false);
  useSeo({ title: "Admin", noindex: true, path: "/experience/admin" });
  if (!unlocked) return <PasscodeGate onUnlock={() => setUnlocked(true)} />;
  const role = sessionStorage.getItem("xp_admin_role") || "owner";
  if (!advanced) return <AdminCream role={role} onAdvanced={role === "owner" ? () => setAdvanced(true) : undefined} />;
  return (
    <div>
      {role !== "manager" && (
        <button data-testid="cream-simple-link" onClick={() => setAdvanced(false)}
          className="fixed top-14 right-2 z-50 text-[10px] font-bold tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1.5">
          ← SIMPLE ADMIN
        </button>
      )}
      <AdminInner />
    </div>
  );
}

function AdminInner() {
  const role = sessionStorage.getItem("xp_admin_role") || "owner";
  const tabs = role === "manager" ? MANAGER_TABS : TABS;
  const [tab, setTab] = useState(tabs[0]);
  const [digest, setDigest] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [tableDetail, setTableDetail] = useState(null);
  const [table, setTable] = useState(() => localStorage.getItem("xp_table") || "");
  const [tableSaved, setTableSaved] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [cinStats, setCinStats] = useState(null);
  const [flags, setFlags] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);
  const [ratings, setRatings] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [specialSlug, setSpecialSlug] = useState("");
  const [specialNote, setSpecialNote] = useState("");
  const [specialDays, setSpecialDays] = useState([]);
  const [qrInput, setQrInput] = useState("");
  const [qrList, setQrList] = useState([]);
  const [tablePicks, setTablePicks] = useState([]);
  const [reviewUrl, setReviewUrl] = useState("");
  const [reviewSaved, setReviewSaved] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [rwName, setRwName] = useState("");
  const [rwCost, setRwCost] = useState("");
  const [rwDish, setRwDish] = useState("");
  const [rwBrand, setRwBrand] = useState("");
  const [costEdits, setCostEdits] = useState({});
  const [quizCode, setQuizCode] = useState("");
  const [quizCodeSaved, setQuizCodeSaved] = useState(false);
  const [championPrize, setChampionPrize] = useState("");
  const [stampBonus, setStampBonus] = useState("50");
  const [stampBonusSaved, setStampBonusSaved] = useState(false);
  const [prizeSaved, setPrizeSaved] = useState(false);
  const [champAwards, setChampAwards] = useState([]);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [checkoutSaved, setCheckoutSaved] = useState(false);
  const [deniedCount, setDeniedCount] = useState(0);

  useEffect(() => {
    if (role === "manager") return undefined;
    if (!localStorage.getItem("xp_access_seen")) localStorage.setItem("xp_access_seen", new Date().toISOString());
    const loadDenied = () =>
      api.get(`/admin/access/denied-count?since=${encodeURIComponent(localStorage.getItem("xp_access_seen") || "")}`)
        .then((r) => setDeniedCount(r.data.count)).catch(() => {});
    loadDenied();
    const t = setInterval(loadDenied, 30000);
    return () => clearInterval(t);
  }, [role]);
  const [earnRate, setEarnRate] = useState("1");
  const [earnRateSaved, setEarnRateSaved] = useState(false);

  const loadRewards = () => {
    api.get("/admin/rewards").then((r) => setRewards(r.data)).catch(() => {});
    api.get("/admin/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
  };
  useEffect(loadRewards, []);

  const addReward = async () => {
    try {
      const r = await api.post("/admin/rewards", { name: rwName.trim(), points_cost: +rwCost, dish_slug: rwDish || null, brand: rwBrand || "" });
      setRewards(r.data);
      setRwName(""); setRwCost(""); setRwDish(""); setRwBrand("");
    } catch {
      alert("Reward needs a name and a points cost of at least 10");
    }
  };
  const removeReward = async (slug) => {
    const r = await api.delete(`/admin/rewards/${slug}`);
    setRewards(r.data);
  };
  const saveCost = async (slug) => {
    const v = +costEdits[slug];
    if (!v) return;
    try {
      const r = await api.patch(`/admin/rewards/${slug}`, { points_cost: v });
      setRewards(r.data);
      setCostEdits((c) => { const n = { ...c }; delete n[slug]; return n; });
      toast.success("Reward cost saved");
    } catch {
      toast.error("Points cost must be between 10 and 100000");
    }
  };
  const saveRewardBrand = async (slug, brand) => {
    try {
      const r = await api.patch(`/admin/rewards/${slug}`, { brand });
      setRewards(r.data);
      toast.success(brand ? "Reward tagged to a brand" : "Reward now redeems anywhere");
    } catch {
      toast.error("Could not update the brand tag");
    }
  };
  const saveQuizCode = async () => {
    try {
      const r = await api.put("/admin/settings", { quiz_code: quizCode });
      setQuizCode(r.data.quiz_code);
      setQuizCodeSaved(true);
      setTimeout(() => setQuizCodeSaved(false), 1500);
    } catch {
      toast.error("Staff code must be 3-12 letters/numbers");
    }
  };
  const savePrize = async () => {
    try {
      const r = await api.put("/admin/settings", { champion_prize: championPrize });
      setChampionPrize(r.data.champion_prize);
      setPrizeSaved(true);
      setTimeout(() => setPrizeSaved(false), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the prize");
    }
  };
  const saveStampBonus = async () => {
    try {
      const r = await api.put("/admin/settings", { stamp_bonus_points: Number(stampBonus) });
      setStampBonus(String(r.data.stamp_bonus_points));
      setStampBonusSaved(true);
      setTimeout(() => setStampBonusSaved(false), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bonus must be 0-500 points");
    }
  };
  const fulfillAward = async (weekOf) => {
    try {
      const r = await api.post(`/admin/champion-awards/${weekOf}/fulfill`);
      setChampAwards(r.data);
    } catch {
      toast.error("Could not mark fulfilled");
    }
  };
  const fulfill = async (code) => {
    await api.post(`/admin/redemptions/${code}/fulfill`);
    loadRewards();
  };

  const saveReviewUrl = async () => {
    try {
      const r = await api.put("/admin/settings", { review_url: reviewUrl });
      setReviewUrl(r.data.review_url);
      setReviewSaved(true);
      setTimeout(() => setReviewSaved(false), 1500);
    } catch {
      alert("Must be a Google link (google.com / maps.app.goo.gl / g.page)");
    }
  };

  const saveCheckoutUrl = async () => {
    try {
      const r = await api.put("/admin/settings", { checkout_url: checkoutUrl });
      setCheckoutUrl(r.data.checkout_url);
      setCheckoutSaved(true);
      setTimeout(() => setCheckoutSaved(false), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Must be a full https:// URL");
    }
  };
  const saveEarnRate = async () => {
    try {
      const r = await api.put("/admin/settings", { spend_earn_rate: Number(earnRate) });
      setEarnRate(String(r.data.spend_earn_rate));
      setEarnRateSaved(true);
      setTimeout(() => setEarnRateSaved(false), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Earn rate must be 0-10 points per dollar");
    }
  };

  const loadOrders = () => api.get("/admin/table-picks").then((r) => setTablePicks(r.data)).catch(() => {});

  useEffect(() => {
    loadOrders();
    const t = setInterval(loadOrders, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/admin/dishes").then((r) => setDishes(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    api.get("/admin/cinematic-stats").then((r) => setCinStats(r.data)).catch(() => {});
    api.get("/admin/digest").then((r) => setDigest(r.data));
    api.get("/admin/craving-heatmap").then((r) => setHeatmap(r.data));
    api.get("/admin/ratings").then((r) => setRatings(r.data));
    api.get("/admin/specials").then((r) => setSpecials(r.data));
    api.get("/admin/settings").then((r) => { setReviewUrl(r.data.review_url); setQuizCode(r.data.quiz_code); setChampionPrize(r.data.champion_prize); setStampBonus(String(r.data.stamp_bonus_points ?? 50)); setCheckoutUrl(r.data.checkout_url || ""); setEarnRate(String(r.data.spend_earn_rate ?? 1)); }).catch(() => {});
    api.get("/admin/champion-awards").then((r) => setChampAwards(r.data)).catch(() => {});
    api.get("/flags").then((r) => setFlags(r.data));
  }, []);

  const addSpecial = async () => {
    const r = await api.post("/admin/specials", { slug: specialSlug, note: specialNote.trim() || null, days: specialDays });
    setSpecials(r.data);
    setSpecialSlug("");
    setSpecialNote("");
    setSpecialDays([]);
  };

  const toggleDay = (d) =>
    setSpecialDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const removeSpecial = async (slug) => {
    const r = await api.delete(`/admin/specials/${slug}`);
    setSpecials(r.data);
  };

  const openTable = async (t) => {
    if (tableDetail?.table === t) return setTableDetail(null);
    try {
      const r = await api.get(`/admin/tables/${encodeURIComponent(t)}`);
      setTableDetail(r.data);
    } catch {
      setTableDetail(null);
    }
  };

  const save = async () => {
    const { slug, spice, creamy, smoky, sweet, rich, tier, featured, pairings, gravies } = editing;
    const r = await api.patch(`/admin/dishes/${slug}`, { spice, creamy, smoky, sweet, rich, tier, featured, pairings, gravies: gravies || [] });
    setDishes(dishes.map((d) => (d.slug === slug ? r.data : d)));
    setSaved(true);
    setTimeout(() => { setSaved(false); setEditing(null); }, 800);
  };

  const toggleFlag = async (flag) => {
    try {
      const r = await api.patch(`/admin/flags/${flag}`, { enabled: !flags[flag] });
      setFlags(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not toggle ${flag}`);
    }
  };

  return (
    <div data-testid="admin-page" className="px-4 pt-6 pb-10 mx-auto w-full max-w-5xl">
      <SectionTitle kicker={`Urban Experience Admin · ${role.toUpperCase()}`} title="Preview control room" testId="admin-title" />
      <PosPushBanner />
      <div className="flex gap-2 mb-6 overflow-x-auto hide-scrollbar -mx-4 px-4 md:flex-wrap md:overflow-visible">
        {tabs.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => {
              setTab(t);
              if (t === "ACCESS") { localStorage.setItem("xp_access_seen", new Date().toISOString()); setDeniedCount(0); }
            }}
            className={`shrink-0 rounded-full px-4 py-2 text-[10px] font-bold tracking-widest border ${tab === t ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/60 border-white/10"}`}>
            {t}
            {t === "ACCESS" && deniedCount > 0 && (
              <span data-testid="access-denied-badge"
                className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#E74C3C] text-white text-[9px] font-black align-middle">
                {deniedCount > 99 ? "99+" : deniedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "WEEKLY DIGEST" && digest && (
        <div data-testid="admin-digest" id="digest-print-area" className="print-area space-y-4">
          <div className="hidden print:block text-center">
            <img src="/urban-logo.png" alt="Urban by VeerGi" className="h-16 mx-auto" />
            <p className="font-display text-2xl font-black mt-2">WEEKLY DIGEST</p>
          </div>
          <button
            data-testid="digest-print-btn"
            onClick={() => window.print()}
            className="no-print w-full border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-bold rounded-full py-3 tracking-widest"
          >
            PRINT / SAVE PDF — FOR STAFF MEETINGS
          </button>
          <p className="text-[10px] tracking-widest text-white/40">WEEK {digest.period.from} → {digest.period.to}</p>
          {digest.pos_alarm?.alarm ? (
            <div data-testid="digest-pos-alarm" className="keep-print-bg rounded-xl border-2 border-[#FF5A4E] bg-[#FF5A4E]/15 p-4">
              <p className="text-[#FF5A4E] font-black text-sm tracking-wide">
                ⛔ POS SYNC ALARM — {[digest.pos_alarm.failed > 0 && `${digest.pos_alarm.failed} ORDER${digest.pos_alarm.failed > 1 ? "S" : ""} FAILED`,
                  digest.pos_alarm.stale_pending > 0 && `${digest.pos_alarm.stale_pending} PENDING OVER AN HOUR`].filter(Boolean).join(" · ")}
              </p>
              <p className="text-xs text-white/60 mt-1">Orders confirmed here that never reached the kitchen POS. Open the staff board → red banner → RETRY SYNC or enter them manually.</p>
            </div>
          ) : (
            <p data-testid="digest-pos-ok" className="text-[10px] tracking-widest text-[#10B981] font-bold">✓ POS SYNC — ALL CLEAR, NO STUCK ORDERS</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[["Engagement events", "events"], ["Visitors", "visitors"], ["Order intents", "order_intents"], ["Ask VeerGi uses", "veergi_uses"]].map(([label, k]) => (
              <div key={k} className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid={`digest-${k}`}>
                <p className="text-2xl font-black text-[#D4AF37]">{digest.this_week[k]}</p>
                <p className="text-[10px] tracking-widest text-white/40 mt-1 mb-1.5">{label.toUpperCase()}</p>
                <Delta v={digest.deltas[k]} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["Stories done", digest.stories_completed], ["Quizzes done", digest.quizzes_completed], ["Points given", digest.points_awarded]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-[#121212] border border-white/10 p-3 text-center">
                <p className="text-lg font-black text-white">{v}</p>
                <p className="text-[9px] tracking-widest text-white/40">{l.toUpperCase()}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="digest-feedback">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">GUEST FEEDBACK THIS WEEK · {digest.ratings_this_week || 0} VERDICTS</p>
            <p className="text-[9px] tracking-widest text-green-400 mb-1">MOST LOVED</p>
            {(digest.most_loved || []).map((r) => (
              <div key={r.slug} className="flex justify-between text-xs py-1" data-testid={`digest-loved-${r.slug}`}>
                <span className="text-white/70">{r.name}</span><span className="font-bold text-green-400">{r.loved} loved</span>
              </div>
            ))}
            {(!digest.most_loved || digest.most_loved.length === 0) && <p className="text-xs text-white/40 pb-1">No loved verdicts yet this week</p>}
            <p className="text-[9px] tracking-widest text-[#E25822] mt-2 mb-1">NEEDS ATTENTION</p>
            {(digest.needs_attention || []).map((r) => (
              <div key={r.slug} className="flex justify-between text-xs py-1" data-testid={`digest-meh-${r.slug}`}>
                <span className="text-white/70">{r.name}</span><span className="font-bold text-[#E25822]">{r.meh} not for me</span>
              </div>
            ))}
            {(!digest.needs_attention || digest.needs_attention.length === 0) && <p className="text-xs text-white/40">Nothing flagged — kitchen is on form</p>}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">TOP DISHES THIS WEEK</p>
            {digest.top_dishes.map((t) => (
              <div key={t.name} className="flex justify-between text-xs py-1"><span className="text-white/70">{t.name}</span><span className="font-bold">{t.count}</span></div>
            ))}
            {digest.top_dishes.length === 0 && <p className="text-xs text-white/40">No dish exploration yet this week</p>}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">TOP CRAVINGS THIS WEEK</p>
            {digest.top_cravings.map((t) => (
              <div key={t.name} className="flex justify-between text-xs py-1"><span className="text-white/70 uppercase">{t.name}</span><span className="font-bold">{t.count}</span></div>
            ))}
            {digest.top_cravings.length === 0 && <p className="text-xs text-white/40">No craving taps yet this week</p>}
          </div>
          {digest.quiz && (
            <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="digest-quiz">
              <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">QUIZ ENGAGEMENT THIS WEEK</p>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[["Plays", digest.quiz.plays], ["Players", digest.quiz.players], ["Correct", `${digest.quiz.correct_pct}%`]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-[#1A1A1A] p-3 text-center">
                    <p className="text-lg font-black text-white">{v}</p>
                    <p className="text-[9px] tracking-widest text-white/40">{l.toUpperCase()}</p>
                  </div>
                ))}
              </div>
              {digest.quiz.top_questions.length > 0 && (
                <>
                  <p className="text-[9px] tracking-widest text-white/40 mb-1">MOST PLAYED QUESTIONS</p>
                  {digest.quiz.top_questions.map((t) => (
                    <div key={t.qid} className="flex justify-between gap-3 text-xs py-1" data-testid={`digest-quizq-${t.qid}`}>
                      <span className="text-white/70 truncate">{t.q}</span>
                      <span className="font-bold shrink-0">{t.plays} · {t.correct_pct}% right</span>
                    </div>
                  ))}
                </>
              )}
              {digest.quiz.champions.length > 0 && (
                <>
                  <p className="text-[9px] tracking-widest text-[#D4AF37] mt-2 mb-1">CURRENT CHAMPIONS</p>
                  {digest.quiz.champions.map((c, i) => (
                    <div key={c.name} className="flex justify-between text-xs py-1" data-testid={`digest-champ-${i}`}>
                      <span className="text-white/70">{i + 1}. {c.name}</span>
                      <span className="font-bold text-[#D4AF37]">{c.points} PTS</span>
                    </div>
                  ))}
                </>
              )}
              {digest.quiz.plays === 0 && <p className="text-xs text-white/40">No quiz plays yet this week</p>}
            </div>
          )}
          {heatmap && (
            <div data-testid="craving-heatmap" className="rounded-xl bg-[#121212] border border-white/10 p-4">
              <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">CRAVING HEATMAP · BY DAY OF WEEK</p>
              <p className="text-[10px] text-white/40 mb-3">Last {heatmap.window_days} days — darker gold = more taps. Prep the kitchen ahead of the spikes.</p>
              {heatmap.rows.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1">
                    <span className="w-20 shrink-0" />
                    {heatmap.days.map((d) => (
                      <span key={d} className="flex-1 text-center text-[8px] tracking-widest text-white/40">{d}</span>
                    ))}
                  </div>
                  {heatmap.rows.map((row) => (
                    <div key={row.craving} className="flex items-center gap-1" data-testid={`heatmap-row-${row.craving}`}>
                      <span className="w-20 shrink-0 text-[9px] tracking-widest text-white/60 uppercase truncate">{row.craving}</span>
                      {row.counts.map((c, i) => (
                        <span
                          key={i}
                          title={`${row.craving} · ${heatmap.days[i]}: ${c}`}
                          className="keep-print-bg flex-1 h-7 rounded flex items-center justify-center text-[9px] font-bold"
                          style={{
                            background: c > 0 ? `rgba(212,175,55,${0.15 + 0.85 * (c / heatmap.max)})` : "rgba(255,255,255,0.04)",
                            color: c / heatmap.max > 0.5 ? "#000" : "rgba(255,255,255,0.55)",
                          }}
                        >
                          {c > 0 ? c : ""}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-white/40">No craving taps in the last {heatmap.window_days} days yet</p>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "DISH INTELLIGENCE" && (
        <div className="space-y-2">
          <p className="text-[10px] text-white/40 mb-3">Prices come from the live Urban menu and are read-only here. Edit taste attributes, tier and featured status.</p>
          {dishes.map((d) => (
            <div key={d.slug} data-testid={`admin-dish-${d.slug}`} className="rounded-xl bg-[#121212] border border-white/10 p-3">
              <div className="flex items-center gap-3">
                <img src={d.image} alt={d.name} className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{d.name}</p>
                  <p className="text-[10px] text-white/40">{d.category} · {money(d.price)} · Tier {d.tier}</p>
                </div>
                <button data-testid={`admin-edit-${d.slug}`} onClick={() => setEditing(editing?.slug === d.slug ? null : { ...d })}
                  className="text-[10px] font-bold text-[#D4AF37]">{editing?.slug === d.slug ? "CLOSE" : "EDIT"}</button>
              </div>
              {editing?.slug === d.slug && (
                <div className="mt-3 border-t border-white/10 pt-3 grid grid-cols-2 gap-2">
                  <NumField label="SPICE" value={editing.spice} onChange={(v) => setEditing({ ...editing, spice: v })} testId="admin-edit-spice" />
                  <NumField label="CREAMY" value={editing.creamy} onChange={(v) => setEditing({ ...editing, creamy: v })} testId="admin-edit-creamy" />
                  <NumField label="SMOKY" value={editing.smoky} onChange={(v) => setEditing({ ...editing, smoky: v })} testId="admin-edit-smoky" />
                  <NumField label="RICH" value={editing.rich} onChange={(v) => setEditing({ ...editing, rich: v })} testId="admin-edit-rich" />
                  <NumField label="TIER" value={editing.tier} onChange={(v) => setEditing({ ...editing, tier: v })} testId="admin-edit-tier" />
                  <label className="flex items-center gap-2 text-xs">
                    <input data-testid="admin-edit-featured" type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#D4AF37]" />
                    <span className="text-white/50">FEATURED</span>
                  </label>
                  <div className="col-span-2 border-t border-white/10 pt-3">
                    <p className="text-[9px] tracking-widest text-[#D4AF37] mb-2">COMPLETE MEAL — WHAT VEERGI SUGGESTS WITH THIS DISH</p>
                    {["main", "bread", "rice", "drink", "dessert"].map((role) => (
                      <label key={role} className="flex items-center justify-between gap-2 text-xs mb-2">
                        <span className="text-white/50 w-16 uppercase shrink-0">{role}</span>
                        <select
                          data-testid={`admin-pair-${role}`}
                          value={editing.pairings?.[role] || ""}
                          onChange={(e) => {
                            const p = { ...(editing.pairings || {}) };
                            if (e.target.value) p[role] = e.target.value;
                            else delete p[role];
                            setEditing({ ...editing, pairings: p });
                          }}
                          className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-2"
                        >
                          <option value="" label="— none —" />
                          {dishes.filter((x) => x.slug !== editing.slug).map((x) => (
                            <option key={x.slug} value={x.slug} label={x.name} />
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                  {editing.category === "Biryani" && (
                    <div className="col-span-2 border-t border-white/10 pt-3" data-testid="admin-gravy-editor">
                      <p className="text-[9px] tracking-widest text-[#D4AF37] mb-1">CHOOSE-YOUR-GRAVY OPTIONS — WHAT GUESTS CAN PAIR WITH THIS BIRYANI</p>
                      <p className="text-[10px] text-white/40 mb-2">Tick the gravies guests may choose. Leave all unticked and VeerGi matches automatically.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {dishes.filter((x) => ["Chicken Curries", "Goat & Lamb Curries", "Vegetarian Curries"].includes(x.category)).map((x) => {
                          const on = (editing.gravies || []).includes(x.slug);
                          return (
                            <label key={x.slug} className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                data-testid={`admin-gravy-${x.slug}`}
                                checked={on}
                                onChange={() => {
                                  const cur = editing.gravies || [];
                                  setEditing({ ...editing, gravies: on ? cur.filter((s) => s !== x.slug) : [...cur, x.slug] });
                                }}
                                className="accent-[#D4AF37]"
                              />
                              <span className={`truncate ${on ? "text-[#D4AF37]" : "text-white/60"}`}>{x.name}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-white/40 mt-2" data-testid="admin-gravy-count">
                        {(editing.gravies || []).length > 0 ? `${(editing.gravies || []).length} gravy option${(editing.gravies || []).length > 1 ? "s" : ""} selected (max 8)` : "Automatic matching active"}
                      </p>
                    </div>
                  )}
                  <button data-testid="admin-save-dish" onClick={save} className="col-span-2 bg-[#D4AF37] text-black text-xs font-bold rounded-full py-2.5 tracking-widest">
                    {saved ? "SAVED ✓" : "SAVE"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "ANALYTICS" && analytics && (
        <div data-testid="admin-analytics" className="space-y-4">
          {analytics.ui_crashes && (
            <div data-testid="crash-panel" className={`rounded-xl border p-4 ${analytics.ui_crashes.count > 0 ? "border-[#E25822]/60 bg-[#E25822]/10" : "border-white/10 bg-[#121212]"}`}>
              <p className={`text-[10px] tracking-widest mb-1 ${analytics.ui_crashes.count > 0 ? "text-[#E25822]" : "text-[#D4AF37]"}`}>
                TABLET CRASH ALERTS · LAST 7 DAYS · {analytics.ui_crashes.count}
              </p>
              {analytics.ui_crashes.count === 0 ? (
                <p className="text-xs text-white/40">No crashes — all tablets healthy</p>
              ) : (
                <>
                  <p className="text-xs text-white/60 mb-2">Guests saw the "Something went wrong" card — details below. If these repeat on one screen, tell your developer.</p>
                  {analytics.ui_crashes.recent.map((c, i) => (
                    <div key={i} data-testid={`crash-row-${i}`} className="text-[11px] py-1.5 border-t border-white/5">
                      <span className="font-bold text-[#E25822]">{c.path || "—"}</span>
                      <span className="text-white/40"> · {c.timestamp ? c.timestamp.slice(0, 16).replace("T", " ") : ""}</span>
                      <p className="text-white/70 mt-0.5 break-words">{c.message}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[["Total events", analytics.total_events], ["Visitors", analytics.visitors],
              ["Order handoffs", analytics.order_handoffs], ["Ask VeerGi uses", analytics.ask_veergi_uses],
              ["Points liability", analytics.points_liability], ["XP → order rate", `${analytics.experience_to_order_rate}%`]].map(([label, v]) => (
              <div key={label} className="rounded-xl bg-[#121212] border border-white/10 p-4">
                <p className="text-2xl font-black text-[#D4AF37]">{v}</p>
                <p className="text-[10px] tracking-widest text-white/40 mt-1">{label.toUpperCase()}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">TOP EXPLORED DISHES</p>
            {analytics.top_dishes.map((t) => (
              <div key={t.slug} className="flex justify-between text-xs py-1"><span className="text-white/70">{t.slug}</span><span className="font-bold">{t.count}</span></div>
            ))}
            {analytics.top_dishes.length === 0 && <p className="text-xs text-white/40">No data yet</p>}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-cinematic-stats">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">CINEMATIC EXPERIENCES · ALL TIME (LAST 7 DAYS)</p>
            <p className="text-[10px] text-white/35 mb-2">The ★ star dish leads the Home banner and attract trailers. With no star, the season decides automatically — Winter: Saag · Spring: Tandoori · Summer: Butter Chicken · Autumn: Dal Makhani. Attach a video clip to a dish in STORIES and it automatically becomes that dish's cinematic hero video.</p>
            {(cinStats?.dishes || []).map((c) => (
              <div key={c.slug} data-testid={`cin-stat-${c.slug}`} className="py-2 border-t border-white/5 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-white/85 flex items-center gap-2">
                    {c.name}
                    {!cinStats.star && cinStats.seasonal?.slug === c.slug && (
                      <span data-testid="cin-seasonal-lead" className="shrink-0 rounded-full border border-[#D4AF37]/40 text-[#D4AF37]/80 px-2 py-0.5 text-[8px] font-black tracking-widest">
                        {cinStats.seasonal.label.toUpperCase()} LEAD
                      </span>
                    )}
                  </p>
                  <button data-testid={`cin-star-${c.slug}`}
                    onClick={async () => {
                      const next = cinStats.star === c.slug ? "" : c.slug;
                      try {
                        await api.put("/admin/cinematic-star", { slug: next });
                        setCinStats({ ...cinStats, star: next });
                        toast.success(next ? `${c.name} leads the Home banner & attract trailers` : "Back to automatic rotation");
                      } catch { toast.error("Could not update star dish"); }
                    }}
                    className={`shrink-0 h-7 px-3 rounded-full text-[9px] font-black tracking-widest border ${cinStats.star === c.slug ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-transparent text-white/50 border-white/15"}`}>
                    {cinStats.star === c.slug ? "★ STAR DISH" : "MAKE STAR"}
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-1.5 text-center">
                  {[["WATCHED", c.opened], ["STORY PLAYS", c.story], ["3D SPINS", c.spins_3d], ["ORDER TAPS", c.orders]].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-[#1A1A1A] py-1.5">
                      <p className="text-sm font-black text-[#D4AF37]">{v.total} <span className="text-[9px] text-white/40 font-bold">({v.week})</span></p>
                      <p className="text-[8px] tracking-widest text-white/40 font-bold">{k}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(!cinStats || cinStats.dishes.length === 0) && <p className="text-xs text-white/40">No cinematic pages yet</p>}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-ratings">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">GUEST FEEDBACK · {ratings?.total_ratings || 0} VERDICTS</p>
            {(ratings?.dishes || []).slice(0, 10).map((r) => (
              <div key={r.slug} data-testid={`rating-row-${r.slug}`} className="flex items-center text-xs py-1.5">
                <span className="text-white/70 truncate flex-1">{r.name}</span>
                <span className="shrink-0 ml-2 font-bold text-[#D4AF37]">{r.loved_pct}% loved</span>
                <span className="shrink-0 ml-3 text-white/40 text-[10px]">L{r.loved} · G{r.good} · M{r.meh}</span>
              </div>
            ))}
            {(!ratings || ratings.dishes.length === 0) && <p className="text-xs text-white/40">No guest verdicts yet — one-tap ratings live on every dish page</p>}
            {(ratings?.recent_notes || []).length > 0 && (
              <>
                <p className="text-[9px] tracking-widest text-[#D4AF37] mt-3 mb-1">WHAT GUESTS ARE SAYING · PRIVATE NOTES</p>
                {ratings.recent_notes.map((n, i) => (
                  <div key={i} data-testid={`feedback-note-${i}`} className="text-[11px] py-1.5 border-t border-white/5">
                    <span className={`font-bold ${n.rating === "loved" ? "text-green-400" : n.rating === "meh" ? "text-[#E25822]" : "text-[#D4AF37]"}`}>{n.rating.toUpperCase()}</span>
                    <span className="text-white/50"> · {n.name}</span>
                    <p className="text-white/75 mt-0.5">"{n.note}"</p>
                  </div>
                ))}
              </>
            )}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">TOP TABLES BY ENGAGEMENT · TAP FOR DETAIL</p>
            {(analytics.top_tables || []).map((t) => (
              <div key={t.table}>
                <button data-testid={`table-row-${t.table}`} onClick={() => openTable(t.table)}
                  className="w-full flex justify-between text-xs py-2 hover:text-[#D4AF37]">
                  <span className="text-white/70">Table {t.table} {tableDetail?.table === t.table ? "▾" : "▸"}</span>
                  <span className="font-bold">{t.count}</span>
                </button>
                {tableDetail?.table === t.table && (
                  <div data-testid={`table-detail-${t.table}`} className="rounded-lg bg-[#1A1A1A] border border-[#D4AF37]/20 p-3 mb-2">
                    <p className="text-[10px] text-white/50 mb-2">{tableDetail.total_events} events · {tableDetail.order_intents} order intents · last active {tableDetail.last_active ? tableDetail.last_active.slice(0, 16).replace("T", " ") : "—"}</p>
                    <p className="text-[9px] tracking-widest text-[#D4AF37]">EXPLORED</p>
                    {tableDetail.top_dishes.map((d) => (
                      <div key={d.slug} className="flex justify-between text-[11px] py-0.5"><span className="text-white/70">{d.slug}</span><span>{d.count}</span></div>
                    ))}
                    {tableDetail.top_dishes.length === 0 && <p className="text-[11px] text-white/40">No dishes explored yet</p>}
                    <p className="text-[9px] tracking-widest text-[#D4AF37] mt-2">CRAVED</p>
                    {tableDetail.top_cravings.map((c) => (
                      <div key={c.craving} className="flex justify-between text-[11px] py-0.5"><span className="text-white/70 uppercase">{c.craving}</span><span>{c.count}</span></div>
                    ))}
                    {tableDetail.top_cravings.length === 0 && <p className="text-[11px] text-white/40">No craving taps yet</p>}
                  </div>
                )}
              </div>
            ))}
            {(!analytics.top_tables || analytics.top_tables.length === 0) && <p className="text-xs text-white/40">No tagged tablets yet — set a table number in the TABLET tab</p>}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">EVENTS</p>
            {analytics.events.slice(0, 12).map((e) => (
              <div key={e.event} className="flex justify-between text-xs py-1"><span className="text-white/70">{e.event}</span><span className="font-bold">{e.count}</span></div>
            ))}
          </div>
        </div>
      )}

      {tab === "ORDERS" && (
        <div data-testid="admin-orders" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] tracking-widest text-white/40">LIVE ORDER LISTS · LAST 6 HOURS · AUTO-REFRESHES</p>
            <button data-testid="orders-refresh" onClick={loadOrders} className="text-[10px] font-bold text-[#D4AF37] tracking-widest">REFRESH</button>
          </div>
          {tablePicks.length === 0 && (
            <p data-testid="orders-empty" className="text-xs text-white/40">No active lists yet — guests build one with ADD TO ORDER LIST</p>
          )}
          <div id="orders-print-area" className="print-area space-y-4">
            {tablePicks.map((t) => (
              <div key={t.user_id} data-testid={`orders-table-${t.table || "untagged"}`} className="rounded-xl bg-[#121212] border border-white/10 p-4">
                <div className="flex justify-between items-center">
                  <p className="text-xs font-black tracking-widest text-[#D4AF37]">{t.table ? `TABLE ${t.table}` : "UNTAGGED DEVICE"}</p>
                  <p className="text-[10px] text-white/40">{new Date(t.updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div className="mt-2 divide-y divide-white/5">
                  {t.items.map((i) => (
                    <div key={i.slug} className="flex justify-between text-xs py-1.5">
                      <span className="text-white/80">{i.qty} × {i.name}</span>
                      <span className="text-white/50">{money(i.price * i.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t border-white/10 text-sm font-bold">
                  <span>TOTAL</span>
                  <span className="text-[#D4AF37]">{money(t.total)}</span>
                </div>
                <button
                  data-testid={`orders-done-${t.table || "untagged"}`}
                  onClick={async () => { await api.post(`/admin/table-picks/${t.user_id}/done`); loadOrders(); }}
                  className="no-print w-full mt-3 bg-[#D4AF37] text-black text-[10px] font-bold rounded-full py-2.5 tracking-widest"
                >
                  MARK AS TAKEN
                </button>
              </div>
            ))}
          </div>
          {tablePicks.length > 0 && (
            <button data-testid="orders-print" onClick={() => window.print()}
              className="no-print w-full border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-bold rounded-full py-3 tracking-widest">
              PRINT LISTS
            </button>
          )}
        </div>
      )}

      {tab === "SPECIALS" && (
        <div data-testid="admin-specials" className="space-y-4">
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">CHEF'S SPECIAL ROTATION</p>
            <p className="text-xs text-white/50 mb-3">Add dishes to the rotation — one is featured on the home screen each day, rotating automatically.</p>
            <select
              data-testid="special-dish-select"
              value={specialSlug}
              onChange={(e) => setSpecialSlug(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm mb-2 focus:outline-none focus:border-[#D4AF37]/60"
            >
              <option value="" label="Choose a dish…" />
              {dishes.map((d) => <option key={d.slug} value={d.slug} label={`${d.name} — ${money(d.price)}`} />)}
            </select>
            <input
              data-testid="special-note-input"
              value={specialNote}
              onChange={(e) => setSpecialNote(e.target.value)}
              placeholder="One-line hook (optional) — e.g. Slow-simmered overnight"
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm mb-3 focus:outline-none focus:border-[#D4AF37]/60"
            />
            <p className="text-[9px] tracking-widest text-white/40 mb-1.5">PIN TO DAYS (OPTIONAL — EMPTY = DAILY ROTATION)</p>
            <div className="flex gap-1.5 mb-3">
              {DAY_LABELS.map((d, i) => (
                <button
                  key={d}
                  data-testid={`special-day-${d.toLowerCase()}`}
                  onClick={() => toggleDay(i)}
                  className={`flex-1 rounded-lg py-2 text-[9px] font-bold tracking-wider border transition-colors ${
                    specialDays.includes(i) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/50"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            <button data-testid="special-add" disabled={!specialSlug} onClick={addSpecial}
              className="w-full bg-[#D4AF37] disabled:bg-[#1A1A1A] disabled:text-white/40 text-black text-xs font-bold rounded-full py-3 tracking-widest">
              ADD TO ROTATION
            </button>
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 divide-y divide-white/5">
            {specials.map((s) => (
              <div key={s.slug} data-testid={`special-row-${s.slug}`} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">
                    {s.name}
                    {s.live_today && <span data-testid="special-live-badge" className="ml-2 text-[8px] font-black text-black bg-[#D4AF37] rounded-full px-2 py-0.5">LIVE TODAY</span>}
                  </p>
                  {s.note && <p className="text-[10px] text-white/40 truncate">{s.note}</p>}
                  <p className="text-[9px] tracking-widest text-white/30 mt-0.5" data-testid={`special-days-${s.slug}`}>
                    {s.days && s.days.length > 0 ? s.days.map((d) => DAY_LABELS[d]).join(" · ") : "DAILY ROTATION"}
                  </p>
                </div>
                <button data-testid={`special-remove-${s.slug}`} onClick={() => removeSpecial(s.slug)} className="text-[10px] font-bold text-[#E25822]">REMOVE</button>
              </div>
            ))}
            {specials.length === 0 && <p className="text-xs text-white/40 p-4">No specials in rotation yet</p>}
          </div>
        </div>
      )}

      {tab === "REWARDS" && (
        <div data-testid="admin-rewards" className="space-y-4">
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">REWARD WALL — WHAT POINTS BUY</p>
            <p className="text-xs text-white/50 mb-3">Guests see these on their Passport and redeem at the table — a code appears on their screen for your server to honour.</p>
            <input
              data-testid="reward-name-input"
              value={rwName}
              onChange={(e) => setRwName(e.target.value)}
              placeholder="Reward name — e.g. Free Mango Lassi"
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm mb-2 focus:outline-none focus:border-[#D4AF37]/60"
            />
            <div className="flex gap-2 mb-3">
              <input
                data-testid="reward-cost-input"
                type="number"
                min="10"
                value={rwCost}
                onChange={(e) => setRwCost(e.target.value)}
                placeholder="Points cost"
                className="w-32 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60"
              />
              <select
                data-testid="reward-dish-select"
                value={rwDish}
                onChange={(e) => setRwDish(e.target.value)}
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60"
              >
                <option value="" label="Link a dish (optional — shows its photo)" />
                {dishes.map((d) => <option key={d.slug} value={d.slug} label={`${d.name} — ${money(d.price)}`} />)}
              </select>
            </div>
            <select
              data-testid="reward-brand-select"
              value={rwBrand}
              onChange={(e) => setRwBrand(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm mb-3 focus:outline-none focus:border-[#D4AF37]/60"
            >
              <option value="" label="Redeems anywhere (optional — tag a brand)" />
              <option value="turban" label="Only at Urban Turban" />
              <option value="halwai" label="Only at Urban Halwai" />
              <option value="bakery" label="Only at Urban Bakery" />
              <option value="express" label="Only at Urban Express" />
              <option value="grocery" label="Only at Urban Grocery" />
            </select>
            <button data-testid="reward-add" disabled={!rwName.trim() || !rwCost} onClick={addReward}
              className="w-full bg-[#D4AF37] disabled:bg-[#1A1A1A] disabled:text-white/40 text-black text-xs font-bold rounded-full py-3 tracking-widest">
              ADD REWARD
            </button>
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 divide-y divide-white/5">
            {rewards.map((r) => (
              <div key={r.slug} data-testid={`admin-reward-${r.slug}`} className="flex items-center gap-2 px-4 py-3 flex-wrap">
                <div className="flex-1 min-w-[45%]">
                  <p className="text-xs font-bold truncate">{r.name}</p>
                  <p className="text-[10px] text-[#D4AF37] font-black tracking-widest mt-0.5">{r.points_cost} PTS{r.dish_slug ? ` · ${r.dish_slug}` : ""}</p>
                </div>
                <select
                  data-testid={`admin-reward-brand-${r.slug}`}
                  value={r.brand || ""}
                  onChange={(e) => saveRewardBrand(r.slug, e.target.value)}
                  className="w-24 shrink-0 bg-[#1A1A1A] border border-white/15 rounded-lg px-1.5 py-1.5 text-[10px] focus:outline-none focus:border-[#D4AF37]/60"
                >
                  <option value="" label="Anywhere" />
                  <option value="turban" label="Turban" />
                  <option value="halwai" label="Halwai" />
                  <option value="bakery" label="Bakery" />
                  <option value="express" label="Express" />
                  <option value="grocery" label="Grocery" />
                </select>
                <input
                  data-testid={`admin-reward-cost-${r.slug}`}
                  type="number"
                  value={costEdits[r.slug] ?? r.points_cost}
                  onChange={(e) => setCostEdits((c) => ({ ...c, [r.slug]: e.target.value }))}
                  className="w-16 shrink-0 bg-[#1A1A1A] border border-white/15 rounded-lg px-2 py-1.5 text-xs text-right focus:outline-none focus:border-[#D4AF37]/60"
                />
                {costEdits[r.slug] !== undefined && +costEdits[r.slug] !== r.points_cost && (
                  <button data-testid={`admin-reward-savecost-${r.slug}`} onClick={() => saveCost(r.slug)}
                    className="shrink-0 text-[9px] font-bold tracking-widest text-black bg-[#D4AF37] rounded-full px-2.5 py-1.5">SAVE</button>
                )}
                <button data-testid={`admin-reward-remove-${r.slug}`} onClick={() => removeReward(r.slug)} className="shrink-0 text-[10px] font-bold text-[#E25822]">REMOVE</button>
              </div>
            ))}
            {rewards.length === 0 && <p className="text-xs text-white/40 p-4">No rewards yet — add the first one above</p>}
          </div>
          <button data-testid="poster-print" onClick={() => window.print()}
            className="no-print w-full border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-bold rounded-full py-3 tracking-widest">
            PRINT REWARD POSTER — FOR TABLES
          </button>
          <div id="poster-print-area" data-testid="reward-poster" className="print-area bg-white rounded-xl p-2 text-center" style={{ border: "3px solid #D4AF37" }}>
            <div className="rounded-lg px-4 py-6" style={{ border: "1px solid rgba(212,175,55,0.6)" }}>
              <img src="/urban-logo.png" alt="Urban by VeerGi" className="h-20 mx-auto object-contain" />
              <p className="font-display text-3xl font-black mt-3 text-black">URBAN PASSPORT REWARDS</p>
              <p className="text-xs mt-1" style={{ color: "#6b5b2e" }}>Play, learn & earn Urban Points with every visit — then treat yourself.</p>
              <div className="mt-5 text-left max-w-xs mx-auto">
                {rewards.map((r) => (
                  <div key={r.slug} className="flex items-baseline justify-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(212,175,55,0.5)" }}>
                    <span className="text-sm font-bold text-black">{r.name}</span>
                    <span className="text-sm font-black shrink-0" style={{ color: "#8a6d1f" }}>{r.points_cost} PTS</span>
                  </div>
                ))}
              </div>
              <div className="inline-block p-2 rounded-lg bg-white mt-5" style={{ border: "2px solid #D4AF37" }}>
                <QRCodeSVG value={`${window.location.origin}/experience`} size={110} fgColor="#111111" />
              </div>
              <p className="text-[9px] tracking-[0.25em] mt-3 font-bold" style={{ color: "#8a6d1f" }}>SCAN · PLAY · EARN · REDEEM AT YOUR TABLE</p>
            </div>
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-stamp-bonus">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">FULL PASSPORT BONUS</p>
            <p className="text-xs text-white/50 mb-3">One-time bonus points a guest earns the moment they collect ALL story stamps. Set 0 to switch the bonus off.</p>
            <div className="flex gap-2">
              <input
                data-testid="stamp-bonus-input"
                value={stampBonus}
                onChange={(e) => { setStampBonus(e.target.value.replace(/\D/g, "")); setStampBonusSaved(false); }}
                className="w-28 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <span className="self-center text-[10px] tracking-widest text-white/40">POINTS</span>
              <button data-testid="stamp-bonus-save" onClick={saveStampBonus}
                className="ml-auto bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {stampBonusSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-earn-rate">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">COUNTER ORDER EARN RATE</p>
            <p className="text-xs text-white/50 mb-3">Points per $1 spent on pickup counter orders synced from the POS (whole dollars, rounded down). Applies to the next order instantly — set 0 to pause spend earning.</p>
            <div className="flex gap-2">
              <input
                data-testid="earn-rate-input"
                value={earnRate}
                onChange={(e) => { setEarnRate(e.target.value.replace(/\D/g, "")); setEarnRateSaved(false); }}
                className="w-28 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <span className="self-center text-[10px] tracking-widest text-white/40">PTS PER $1</span>
              <button data-testid="earn-rate-save" onClick={saveEarnRate}
                className="ml-auto bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {earnRateSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-champion-prize">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">WEEKLY CHAMPION PRIZE</p>
            <p className="text-xs text-white/50 mb-3">Every Monday, last week's top quiz table automatically gets a prize code on their Passport. Set what they win:</p>
            <div className="flex gap-2">
              <input
                data-testid="champion-prize-input"
                value={championPrize}
                onChange={(e) => { setChampionPrize(e.target.value); setPrizeSaved(false); }}
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button data-testid="champion-prize-save" onClick={savePrize}
                className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {prizeSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
            {champAwards.length > 0 && (
              <div className="mt-4 divide-y divide-white/5">
                {champAwards.map((a) => (
                  <div key={a.week_of} data-testid={`champion-award-${a.week_of}`} className="flex items-center gap-2 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{a.winner} · {a.points} PTS</p>
                      <p className="text-[10px] text-white/40">WEEK OF {a.week_of} · {a.prize}</p>
                    </div>
                    <span className="font-display text-sm font-bold text-[#D4AF37] tracking-[0.1em] shrink-0">{a.code}</span>
                    {a.status === "pending" ? (
                      <button data-testid={`champion-fulfill-${a.week_of}`} onClick={() => fulfillAward(a.week_of)}
                        className="shrink-0 text-[9px] font-bold tracking-widest text-black bg-[#D4AF37] rounded-full px-2.5 py-1.5">FULFILL</button>
                    ) : (
                      <span className="shrink-0 text-[9px] tracking-widest text-green-400 font-bold">DONE ✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl bg-[#121212] border border-white/10 p-4" data-testid="admin-redemptions">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-2">RECENT REDEMPTIONS</p>
            {redemptions.map((m) => (
              <div key={m.code} data-testid={`redemption-row-${m.code}`} className="flex items-center gap-3 py-2 border-t border-white/5 first:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{m.reward_name}</p>
                  <p className="text-[10px] text-white/40">{m.created_at?.slice(0, 16).replace("T", " ")} · {m.user_id}</p>
                </div>
                <span className="font-display text-base font-bold text-[#D4AF37] tracking-[0.15em] shrink-0">{m.code}</span>
                {m.status === "issued" ? (
                  <button data-testid={`redemption-fulfill-${m.code}`} onClick={() => fulfill(m.code)}
                    className="shrink-0 text-[9px] font-bold tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1.5">
                    MARK USED
                  </button>
                ) : (
                  <span className="shrink-0 text-[9px] font-bold tracking-widest text-white/40 border border-white/15 rounded-full px-3 py-1.5">USED</span>
                )}
              </div>
            ))}
            {redemptions.length === 0 && <p className="text-xs text-white/40">No redemptions yet — guests redeem from their Passport reward wall</p>}
          </div>
        </div>
      )}

      {tab === "SITE" && <AdminSite />}
      {tab === "VISITS" && <AdminVisits />}
      {tab === "QUIZZES" && <AdminQuizzes />}

      {tab === "STORY STUDIO" && <AdminStories />}
      {tab === "CLIPS" && <AdminClips />}

      {tab === "EXTENDED MENU" && <AdminExtMenu />}

      {tab === "ARCADE" && <AdminArcade />}
      {tab === "ACCESS" && <AdminAccess />}

      {tab === "BRANDS" && <AdminBrands />}

      {tab === "VOICE" && <AdminVoice />}

      {tab === "TABLET" && (
        <>
        <PosPingPanel />
        <div data-testid="admin-tablet" className="rounded-xl bg-[#121212] border border-white/10 p-5">
          <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">THIS TABLET'S TABLE NUMBER</p>
          <p className="text-xs text-white/50 mb-4">Tag this tablet with its table so analytics show which tables explore most. Stored on this device only.</p>
          <div className="flex gap-2">
            <input
              data-testid="admin-table-input"
              value={table}
              onChange={(e) => { setTable(e.target.value); setTableSaved(false); }}
              placeholder="e.g. 12"
              className="flex-1 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60"
            />
            <button
              data-testid="admin-table-save"
              onClick={() => {
                const v = table.trim();
                if (v) localStorage.setItem("xp_table", v);
                else localStorage.removeItem("xp_table");
                window.dispatchEvent(new Event("xp-table-changed"));
                setTableSaved(true);
              }}
              className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest"
            >
              {tableSaved ? "SAVED ✓" : "SAVE"}
            </button>
          </div>
          <p data-testid="admin-table-current" className="text-xs text-white/50 mt-4">
            Currently tagged: {localStorage.getItem("xp_table") ? `Table ${localStorage.getItem("xp_table")}` : "not tagged"}
          </p>
          <PosMenuKeyCard />
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">GUEST IDENTITY ON THIS TABLET</p>
            <p className="text-xs text-white/50 mb-4">Current guest: <span className="font-mono text-white/70" data-testid="admin-current-guest">{USER_ID}</span></p>
            <div className="flex gap-2">
              <button data-testid="admin-new-guest" onClick={newGuest}
                className="flex-1 border border-white/20 text-white/80 text-xs font-bold rounded-full py-3 tracking-widest">
                START NEW GUEST
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">PRINTABLE TABLE QR CARDS</p>
            <p className="text-xs text-white/50 mb-4">Each card opens the MENU with its table already set — guests scan and start picking dishes right away. The table sticks for their whole visit and shows on screen, tappable to fix.</p>
            <div className="flex gap-2">
              <input
                data-testid="qr-tables-input"
                value={qrInput}
                onChange={(e) => setQrInput(e.target.value)}
                placeholder="e.g. 12 (tables 1–12) or 1,2,5A"
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button data-testid="qr-generate" onClick={() => setQrList(parseTables(qrInput))}
                className="shrink-0 bg-[#D4AF37] text-black text-xs font-bold rounded-full px-5 tracking-widest">
                GENERATE
              </button>
            </div>
            {qrList.length > 0 && (
              <>
                <button data-testid="qr-print" onClick={() => window.print()}
                  className="w-full mt-4 border border-[#D4AF37]/60 text-[#D4AF37] text-xs font-bold rounded-full py-3 tracking-widest">
                  PRINT {qrList.length} CARD{qrList.length > 1 ? "S" : ""}
                </button>
                <div id="qr-print-area" data-testid="qr-print-area" className="print-area grid grid-cols-1 gap-3 mt-4">
                  {qrList.map((t) => (
                    <div key={t} data-testid={`qr-card-${t}`} className="qr-card bg-white rounded-xl p-1.5" style={{ border: "3px solid #D4AF37" }}>
                      <div className="rounded-lg px-3 py-3" style={{ border: "1px solid rgba(212,175,55,0.6)" }}>
                        <img src="/urban-logo.png" alt="Urban by VeerGi" className="h-12 mx-auto object-contain" />
                        <div className="flex items-center justify-center gap-4 mt-2">
                          <div className="text-center shrink-0">
                            <p className="text-[10px] tracking-[0.3em] font-black" style={{ color: "#8a6d1f" }}>TABLE</p>
                            <p data-testid={`qr-card-number-${t}`} className="font-display font-black text-black leading-none" style={{ fontSize: "56px" }}>{t}</p>
                          </div>
                          <div className="inline-block p-2 rounded-lg bg-white shrink-0" style={{ border: "2px solid #D4AF37" }}>
                            <QRCodeSVG value={`${window.location.origin}/menu?table=${encodeURIComponent(t)}`} size={130} fgColor="#111111" className="mx-auto" />
                          </div>
                        </div>
                        <p className="text-[9px] tracking-[0.25em] mt-2.5 font-bold text-center" style={{ color: "#8a6d1f" }}>SCAN · SEE THE MENU · ORDER FROM YOUR TABLE</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">GOOGLE REVIEW LINK</p>
            <p className="text-xs text-white/50 mb-3">Paste your exact Google review or listing URL — the "LOVED IT? GOOGLE REVIEW" button on dish pages will use it. Leave empty for the default Google Maps search.</p>
            <div className="flex gap-2">
              <input
                data-testid="review-url-input"
                value={reviewUrl}
                onChange={(e) => setReviewUrl(e.target.value)}
                placeholder="https://g.page/r/… or https://www.google.com/maps/…"
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button data-testid="review-url-save" onClick={saveReviewUrl}
                className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {reviewSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">PICKUP CHECKOUT URL</p>
            <p className="text-xs text-white/50 mb-3">Where the order sheet's "PICKUP CHECKOUT — PAY AT COUNTER" button sends guests (opens in a new tab, no payment in this app). Paste your exact ordering-page URL; leave empty to reset to https://tryurbanturban.com.</p>
            <div className="flex gap-2">
              <input
                data-testid="checkout-url-input"
                value={checkoutUrl}
                onChange={(e) => { setCheckoutUrl(e.target.value); setCheckoutSaved(false); }}
                placeholder="https://tryurbanturban.com/…"
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button data-testid="checkout-url-save" onClick={saveCheckoutUrl}
                className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {checkoutSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">LEVEL 3 QUIZ — STAFF CODE</p>
            <p className="text-xs text-white/50 mb-3">Guests unlock the 50-pt "Taste & Expert" quizzes automatically when they build an order list — or a server can share this code. Change it anytime.</p>
            <div className="flex gap-2">
              <input
                data-testid="quiz-code-setting-input"
                value={quizCode}
                onChange={(e) => { setQuizCode(e.target.value.toUpperCase()); setQuizCodeSaved(false); }}
                placeholder="e.g. URBAN"
                className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-5 py-3 text-xs tracking-widest uppercase focus:outline-none focus:border-[#D4AF37]/60"
              />
              <button data-testid="quiz-code-setting-save" onClick={saveQuizCode}
                className="bg-[#D4AF37] text-black text-xs font-bold rounded-full px-6 tracking-widest shrink-0">
                {quizCodeSaved ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-5 pt-5">
            <p className="text-[10px] tracking-widest text-[#D4AF37] mb-1">POS CONNECTION &amp; SYNC</p>
            <p className="text-xs text-white/50 mb-4">Plumbing for the VeerGi Galla POS — the Extended Menu tab stays a clean toggle list.</p>
            <PosDiagnostics />
          </div>
        </div>
        </>
      )}

      {tab === "FEATURE FLAGS" && flags && (
        <div data-testid="admin-flags" className="rounded-xl bg-[#121212] border border-white/10 divide-y divide-white/5">
          {Object.entries(flags).filter(([flag, enabled]) => typeof enabled === "boolean" && flag !== "voice_ready").map(([flag, enabled]) => (
            <div key={flag} className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-mono text-white/70">{flag}</span>
              <button data-testid={`flag-toggle-${flag}`} onClick={() => toggleFlag(flag)} aria-pressed={enabled}
                className={`w-11 h-6 rounded-full relative transition-colors ${enabled ? "bg-[#D4AF37]" : "bg-white/15"}`} aria-label={`Toggle ${flag}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
